import Date from "common/values/date/date";
import Guid from "common/values/guid/guid";
import _ from "lodash";

export default class PermissionFilterParameters {
  public hasParent?: boolean;
  public resource?: unknown;
  public parentResource?: unknown;
  public parentId?: Guid;
  public legalEntityId?: Guid;
  public granteeUserIds?: Guid[];
  public grantorUserId?: Guid;
  public maxCreatedDate?: Date;
  public minCreatedDate?: Date;
  public showDisabled?: boolean;
  public minDisabledDate?: Date;
  public maxDisabledDate?: Date;
  public categories?: string[];
  public accessType?: string;
  public name?: string;

  constructor(
    hasParent?: boolean,
    resource?: unknown,
    parentResource?: unknown,
    parentId?: Guid,
    legalEntityId?: Guid,
    granteeUserIds?: Guid[],
    grantorUserId?: Guid,
    maxCreatedDate?: Date,
    minCreatedDate?: Date,
    showDisabled?: boolean,
    minDisabledDate?: Date,
    maxDisabledDate?: Date,
    categories?: string[],
    accessType?: string,
    name?: string
  ) {
    this.hasParent = hasParent;
    this.resource = resource;
    this.parentResource = parentResource;
    this.parentId = parentId;
    this.legalEntityId = legalEntityId;
    this.granteeUserIds = granteeUserIds;
    this.grantorUserId = grantorUserId;
    this.maxCreatedDate = maxCreatedDate;
    this.minCreatedDate = minCreatedDate;
    this.showDisabled = showDisabled;
    this.minDisabledDate = minDisabledDate;
    this.maxDisabledDate = maxDisabledDate;
    this.categories = categories;
    this.accessType = accessType;
    this.name = name;
  }

  public asSearchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (this.hasParent !== undefined) searchParams.append("hasParent", String(this.hasParent));
    if (this.resource) {
      const resourceString = typeof this.resource === "string" ? this.resource : JSON.stringify(this.resource);
      searchParams.append("resource", resourceString);
    }
    if (this.parentResource) {
      const parentResourceString = typeof this.parentResource === "string" ? this.parentResource : JSON.stringify(this.parentResource);
      searchParams.append("parentResource", parentResourceString);
    }
    if (this.parentId) searchParams.append("parentId", this.parentId.toString());
    if (this.legalEntityId) searchParams.append("legalEntityId", this.legalEntityId.toString());

    if (this.granteeUserIds?.length) {
      for (const id of this.granteeUserIds) {
        searchParams.append("granteeUserIds", id.toString());
      }
    }

    if (this.grantorUserId) searchParams.append("grantorUserId", this.grantorUserId.toString());
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
    if (this.showDisabled !== undefined) searchParams.append("showDisabled", String(this.showDisabled));
    if (this.maxDisabledDate) searchParams.append("maxDisabledDate", this.maxDisabledDate.toISOString());
    if (this.minDisabledDate) searchParams.append("minDisabledDate", this.minDisabledDate.toISOString());

    if (this.categories?.length) {
      for (const c of this.categories) {
        searchParams.append("categories", c);
      }
    }

    if (this.accessType) searchParams.append("accessType", this.accessType);
    if (this.name) searchParams.append("name", this.name);

    return searchParams;
  }


  public isEqualTo(other: PermissionFilterParameters | undefined): boolean {
    if (!other) return false;

    const granteeEqual =
      (!this.granteeUserIds && !other.granteeUserIds) ||
      (!!this.granteeUserIds &&
        !!other.granteeUserIds &&
        this.granteeUserIds.length === other.granteeUserIds.length &&
        this.granteeUserIds.every(id =>
          other.granteeUserIds?.some(otherId => otherId.isEqualTo(id))
        ));

    const categoriesEqual =
      (!this.categories && !other.categories) ||
      (!!this.categories && !!other.categories &&
        this.categories.length === other.categories.length &&
        this.categories.every(c => other.categories!.includes(c)));

    return (
      this.hasParent === other.hasParent &&
      _.isEqual(this.resource, other.resource) &&
      _.isEqual(this.parentResource, other.parentResource) &&
      (!!this.parentId === !!other.parentId
        ? this.parentId?.isEqualTo(other.parentId) ?? true
        : false) &&
      (!!this.legalEntityId === !!other.legalEntityId
        ? this.legalEntityId?.isEqualTo(other.legalEntityId) ?? true
        : false) &&
      granteeEqual &&
      (!!this.grantorUserId === !!other.grantorUserId
        ? this.grantorUserId?.isEqualTo(other.grantorUserId) ?? true
        : false) &&
      (!!this.maxCreatedDate === !!other.maxCreatedDate
        ? this.maxCreatedDate?.isEqualTo(other.maxCreatedDate) ?? true
        : false) &&
      (!!this.minCreatedDate === !!other.minCreatedDate
        ? this.minCreatedDate?.isEqualTo(other.minCreatedDate) ?? true
        : false) &&
      this.showDisabled === other.showDisabled &&
      (!!this.maxDisabledDate === !!other.maxDisabledDate
        ? this.maxDisabledDate?.isEqualTo(other.maxDisabledDate) ?? true
        : false) &&
      (!!this.minDisabledDate === !!other.minDisabledDate
        ? this.minDisabledDate?.isEqualTo(other.minDisabledDate) ?? true
        : false) &&
      categoriesEqual &&
      this.accessType === other.accessType &&
      this.name === other.name
    );
  }
}
